class ProductLifestyleGallery extends HTMLElement {
  #controller;
  #activeSlideIndex = 0;
  #isNavigating = false;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const track = this.querySelector('.plg__track');
    if (!track) return;

    // Scroll sync (RAF-throttled)
    let ticking = false;
    track.addEventListener('scroll', () => {
      if (this.#isNavigating || ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const scrollCenter = track.scrollLeft + track.offsetWidth / 2;
        const slides = track.querySelectorAll('.plg__slide');
        let closest = 0, minDist = Infinity;
        slides.forEach((slide, i) => {
          const center = slide.offsetLeft + slide.offsetWidth / 2;
          const dist = Math.abs(scrollCenter - center);
          if (dist < minDist) { minDist = dist; closest = i; }
        });
        if (closest !== this.#activeSlideIndex) {
          this.#activeSlideIndex = closest;
          this.#updateActiveSlide(closest);
          this.#updateDots(closest);
          this.#updateArrows(closest);
        }
        ticking = false;
      });
    }, { signal, passive: true });

    // Dot clicks
    this.querySelectorAll('.plg__dot').forEach(dot => {
      dot.addEventListener('click', () => {
        const idx = parseInt(dot.dataset.slideIndex, 10);
        this.#navigateToSlide(idx);
      }, { signal });
    });

    // Slide clicks (navigate to clicked slide)
    track.querySelectorAll('.plg__slide').forEach(slide => {
      slide.addEventListener('click', () => {
        const idx = parseInt(slide.dataset.slideIndex, 10);
        if (idx !== this.#activeSlideIndex) this.#navigateToSlide(idx);
      }, { signal });
    });

    // Arrow clicks
    const prevBtn = this.querySelector('.plg__arrow--prev');
    const nextBtn = this.querySelector('.plg__arrow--next');
    prevBtn?.addEventListener('click', () => {
      if (this.#activeSlideIndex > 0) this.#navigateToSlide(this.#activeSlideIndex - 1);
    }, { signal });
    nextBtn?.addEventListener('click', () => {
      const count = track.querySelectorAll('.plg__slide').length;
      if (this.#activeSlideIndex < count - 1) this.#navigateToSlide(this.#activeSlideIndex + 1);
    }, { signal });

    // Init first slide
    this.#updateActiveSlide(0);
  }

  disconnectedCallback() { this.#controller?.abort(); }

  #navigateToSlide(idx) {
    const track = this.querySelector('.plg__track');
    const slide = track?.querySelectorAll('.plg__slide')[idx];
    if (!slide) return;
    this.#isNavigating = true;
    const targetLeft = slide.offsetLeft - (track.offsetWidth - slide.offsetWidth) / 2;
    track.scrollTo({ left: targetLeft, behavior: 'smooth' });
    this.#activeSlideIndex = idx;
    this.#updateActiveSlide(idx);
    this.#updateDots(idx);
    this.#updateArrows(idx);
    setTimeout(() => { this.#isNavigating = false; }, 400);
  }

  #updateActiveSlide(idx) {
    this.querySelectorAll('.plg__slide').forEach((s, i) => {
      s.classList.toggle('plg__slide--active', i === idx);
    });
  }

  #updateDots(idx) {
    this.querySelectorAll('.plg__dot').forEach((d, i) => {
      const active = i === idx;
      d.classList.toggle('plg__dot--active', active);
      d.setAttribute('aria-selected', String(active));
    });
  }

  #updateArrows(idx) {
    const count = this.querySelector('.plg__track')?.querySelectorAll('.plg__slide').length ?? 0;
    const prev = this.querySelector('.plg__arrow--prev');
    const next = this.querySelector('.plg__arrow--next');
    prev?.classList.toggle('plg__arrow--hidden', idx === 0);
    next?.classList.toggle('plg__arrow--hidden', idx === count - 1);
  }
}

customElements.define('product-lifestyle-gallery', ProductLifestyleGallery);
